// ============================================================================
// OrgChart — canvas zoomable/deplacable de l'organigramme (E1c, F-237)
// ============================================================================
// Portage du PoC (components/org/org-chart.tsx) au standard maison. Recoit l'arbre
// DEJA construit (noeuds 100 % serialisables — lib/org/hierarchy, DEJA porte) ;
// gere ici l'etat d'INTERACTION : zoom/pan (react-zoom-pan-pinch, dep autorisee
// decision 3) et repli des branches (Set d'emails). Le `ref` expose pointe le noeud
// `.org-tree` — c'est LUI que html-to-image capture pour les exports (F-235/F-236).
//
// ECART ASSUME vs le PoC : la garde « clic-vs-pan » (onClickCapture + seuil 5 px)
// n'est PAS portee. Elle n'existait que parce que les cartes etaient des LIENS de
// navigation — un drag qui finit sur une carte ne devait pas naviguer. Les cartes
// ne sont plus cliquables (decision gravee 1, /profile non portee) : il n'y a plus
// de navigation a avaler. Le toggle de branche, lui, est un vrai clic sur une cible
// de 24 px que le pan ne declenche pas. A RE-PORTER avec [GEST-PROFILE-SURFACE].

import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch';
import { FoldVertical, Maximize, Network, UnfoldVertical, ZoomIn, ZoomOut } from 'lucide-react';
import { EmptyState, GS_COMPONENTS } from '@gs/ui';
import { collectAncestorEmails, collectExpandableEmails, type OrgNode } from '../../lib/org/hierarchy';
import { useTranslation } from '../../i18n';
import { OrgTreeNode } from './OrgTreeNode';

const INITIAL_SCALE = 0.7;

// Les controles du canvas flottent AU-DESSUS de l'arbre : btnSecondary est
// `bg-transparent` (illisible par-dessus les connecteurs) et n'a pas de
// touch-manipulation. On COMPOSE un fond opaque + cible tactile — aucun token neuf.
const OVERLAY_BTN =
  'inline-flex items-center bg-white shadow-sm touch-manipulation dark:bg-slate-800';

// Demande de centrage venue de la recherche : l'email cible + un compteur qui
// change a chaque selection (meme personne re-selectionnee -> l'effet re-tire).
export type OrgFocus = { email: string; key: number };

export const OrgChart = forwardRef<
  HTMLDivElement,
  {
    roots: OrgNode[];
    highlightedEmail?: string | null;
    focus?: OrgFocus | null;
  }
>(function OrgChart({ roots, highlightedEmail, focus }, treeRef) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const expandable = useMemo(() => collectExpandableEmails(roots), [roots]);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);

  // Recherche -> centrage (F-234) : deplier tous les ancetres de la cible (pour
  // qu'elle soit rendue) puis, apres la mise en page (double rAF), zoomer dessus.
  useEffect(() => {
    if (!focus) return;
    const ancestors = collectAncestorEmails(roots, focus.email);
    setCollapsed((prev) => {
      if (ancestors.length === 0) return prev;
      const next = new Set(prev);
      for (const a of ancestors) next.delete(a);
      return next;
    });
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        transformRef.current?.zoomToElement(`org-node-${focus.email}`, 1, 400);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // Re-tirer a CHAQUE selection via focus.key, pas sur chaque changement
    // d'identite de `roots` (qui se reconstruit a chaque filtre).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.key]);

  function toggle(email: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  if (roots.length === 0) {
    return (
      <EmptyState
        icon={<Network size={48} aria-hidden="true" />}
        title={t('gest:organigramme.chart.emptyTitle')}
        description={t('gest:organigramme.chart.emptyDescription')}
      />
    );
  }

  return (
    // min-h-96 (384px) : plancher du canvas sur viewport court. Le PoC posait
    // 420px, hors barème Tailwind (#28/DS-015) — la hauteur réelle vient de
    // h-[calc(100svh-280px)], ce plancher ne joue que sur les très petits écrans.
    <div
      data-org-canvas
      className="relative h-[calc(100svh-280px)] min-h-96 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm dark:border-slate-700 dark:bg-slate-900"
    >
      <TransformWrapper
        ref={transformRef}
        initialScale={INITIAL_SCALE}
        minScale={0.25}
        maxScale={2}
        centerOnInit
        limitToBounds={false}
        wheel={{ step: 0.15 }}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
      >
        {({ zoomIn, zoomOut, resetTransform, centerView }) => (
          <>
            <div className="absolute right-3 top-3 z-10 flex gap-1.5">
              <button
                type="button"
                onClick={() => setCollapsed(new Set())}
                disabled={collapsed.size === 0}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} gap-1.5 disabled:opacity-50`}
              >
                <UnfoldVertical size={16} aria-hidden="true" />
                {t('gest:organigramme.chart.expandAll')}
              </button>
              <button
                type="button"
                onClick={() => setCollapsed(new Set(expandable))}
                disabled={collapsed.size === expandable.length}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} gap-1.5 disabled:opacity-50`}
              >
                <FoldVertical size={16} aria-hidden="true" />
                {t('gest:organigramme.chart.collapseAll')}
              </button>
            </div>

            <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-1">
              <button
                type="button"
                aria-label={t('gest:organigramme.chart.zoomIn')}
                onClick={() => zoomIn()}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} justify-center`}
              >
                <ZoomIn size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('gest:organigramme.chart.zoomOut')}
                onClick={() => zoomOut()}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} justify-center`}
              >
                <ZoomOut size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('gest:organigramme.chart.resetView')}
                onClick={() => {
                  resetTransform(0);
                  centerView(INITIAL_SCALE, 200);
                }}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} justify-center`}
              >
                <Maximize size={16} aria-hidden="true" />
              </button>
            </div>

            <TransformComponent wrapperClass="!h-full !w-full" contentClass="p-12">
              {/* w-max : le bloc epouse la largeur naturelle de l'arbre, pour que le
                  canvas (fit-content) englobe tout — sinon les branches larges
                  deborderaient du cadre centre. */}
              <div ref={treeRef} className="org-tree w-max">
                <ul>
                  {roots.map((root) => (
                    <OrgTreeNode
                      key={root.email}
                      node={root}
                      depth={0}
                      collapsed={collapsed}
                      highlightedEmail={highlightedEmail}
                      onToggle={toggle}
                    />
                  ))}
                </ul>
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
});
